import { readPartnerChecklistBaseline } from '@/services/checklist/partnerChecklistBaseline';
import type { ChecklistMediaConflictCommit } from '@/services/sync/ChecklistMediaConflictContract';
import type { ChecklistMediaObservationBaselineInput } from '@/services/sync/ChecklistMediaObservationContract';
import type { ChecklistMediaCanonicalAdoptionInput } from '@/services/sync/ChecklistMediaCanonicalAdoptionContract';
import { observationFieldId, type ChecklistAnswerPatch } from './checklist/checklistObservations';
import type { HistoricalInsertGuard } from './sync/HistoricalInsertGuard';
import type { ArchivedReplayCommit } from '@/services/sync/ArchivedPartialReplayCoordinator';
import { Platform } from 'react-native';
import {
  type CanonicalMetadataCandidateScope,
  type ChecklistMediaCompletionInput,
  type ChecklistMediaFinalizationInput,
  type ChecklistMediaPendingInput,
  type ChecklistSnapshotAtomicCreateInput,
  type ChecklistSnapshotCanonicalReconciliationInput,
  type ChecklistStructuralFieldMutation,
  type ChecklistStructuralMutationInput,
  type ChecklistStructuralMutationResult,
  type DatabaseMutationInput,
  IDatabaseAdapter,
} from '../adapters/IDatabaseAdapter';
import DexieAdapter from '../adapters/DexieAdapter';
import { databaseActions, globalState } from '../state/globalState';
import { DatabaseLogger, DatabaseServiceLogger } from '../utils/logger';
import { APP_CONSTANTS } from '../config/constants';
import { createSupportConflictClassifier } from '@/services/diagnostics/classifySupportConflictEffect';
import { isActiveSyncConflict } from '@/services/sync/PendingOperationTerminalization';
import type { PendingOperation, PendingOperationRaw } from './syncService';
import { debugPause } from '@/utils/debugPauseUniversal';
import { v4 as uuidv4 } from 'uuid';
import { isLogBufferEnabled, recordBufferedLogEvent } from '@/utils/logBuffer';
import { ChecklistLogBufferService } from '@/services/logging/ChecklistLogBufferService';
import { sanitizeLocalTablePayload, sanitizeLocalTableRows } from '@/utils/localTableSchema';
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from '@/config/recoveryDiagnostic';
import { IS_CHECKLIST_SYNC_RECOVERY_CANARY } from '@/config/recoveryCanary';
import type {
  RecoveryCaptureInput,
  RecoveryConsistentSnapshot,
  RecoveryResetContext,
} from '@/types/recovery';
import type { SupportFullStateSnapshot } from '@/types/supportFullState';
import { isRecoveryReceiptVerifiedInProcess } from '@/services/recovery/Ed25519RecoveryReceiptVerifier';
import { isRecoveryRuntimeBlocked } from '@/services/recovery/RecoveryRuntimeGate';
import { capturePendingWorkOrigin, type PendingWorkOrigin } from '@/services/sync/PendingSyncSession';

export const shouldBlockUnscopedDatabaseClear = (
  diagnosticEnabled: boolean,
  recoveryCanaryEnabled: boolean,
  platform: string
): boolean =>
  diagnosticEnabled || (recoveryCanaryEnabled && platform !== 'web');

export const isSupportResetAuthorizationValid = (input: {
  companyId: string; userId: string; checkedAt: string;
  currentCompanyId?: string; currentUserId?: string; recoveryBlocked: boolean; now?: number;
}): boolean => {
  const age = (input.now ?? Date.now()) - Date.parse(input.checkedAt);
  return input.currentCompanyId === input.companyId && input.currentUserId === input.userId &&
    Number.isFinite(age) && age >= 0 && age <= 15_000 && !input.recoveryBlocked;
};

type SupportScopeReason = 'other_company' | 'contradictory_links' | 'unlinked';
type SupportScopeCountsByTable = Record<string, Partial<Record<SupportScopeReason, number>>>;
type SupportScopeRows = {
  operations: Record<string, any>[];
  images: Record<string, any>[];
  deletions: Record<string, any>[];
  conflicts: Record<string, any>[];
  conflictEffects: Record<string, number>;
  conflictSummaryRows: Record<string, any>[];
  scopeCountsByTable: SupportScopeCountsByTable;
};

export const classifySupportLocalScope = (
  tables: Record<string, Record<string, any>[]>,
  companyId: string,
  userId: string
): SupportScopeRows & { safe: boolean } => {
  const companyTable = APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME;
  const scopedIds = new Set<string>();
  let safe = true;
  Object.entries(tables).forEach(([table, rows]) => rows.forEach(row => {
    const id = typeof row.id === 'string' ? row.id : '';
    if (table === companyTable && id) {
      if (id === companyId) scopedIds.add(id); else safe = false;
    }
    if (typeof row.empresa_id === 'string' && row.empresa_id) {
      if (row.empresa_id === companyId) {
        if (id) scopedIds.add(id);
      } else {
        safe = false;
      }
    }
  }));
  const matchesPath = (path: unknown): boolean => {
    const value = String(path ?? '');
    return value.startsWith(`${companyId}/`) || value.includes(`/${companyId}/`) ||
      value.startsWith(`${userId}/`) || value.includes(`/${userId}/`);
  };
  const matchesItem = (row: Record<string, any>): boolean =>
    scopedIds.has(String(row.item_id ?? row.itemId ?? row.campo_id ?? '')) || matchesPath(row.path);
  const operations = (tables.pending_operations ?? []).filter(row =>
    row.user_id === userId || (!row.user_id && matchesItem(row))
  );
  const images = (tables.pending_images ?? []).filter(matchesItem);
  const deletions = (tables.pending_deletions ?? []).filter(matchesItem);
  const scopedConflicts = (tables.sync_conflicts ?? []).filter(row =>
    row.empresa_id === companyId && (!row.user_id || row.user_id === userId)
  );
  if (operations.length !== (tables.pending_operations ?? []).length ||
      images.length !== (tables.pending_images ?? []).length ||
      deletions.length !== (tables.pending_deletions ?? []).length ||
      scopedConflicts.length !== (tables.sync_conflicts ?? []).length) safe = false;
  const conflicts = scopedConflicts.filter(isActiveSyncConflict);
  // O resumo conserva estados desconhecidos; a guarda existente usa conflicts.
  const conflictSummaryRows = scopedConflicts.filter(row => !['resolved', 'ignored'].includes(String(row.status ?? '').toLowerCase()));
  const conflictEffects: Record<string, number> = {};
  const classifyConflict = createSupportConflictClassifier(tables);
  conflictSummaryRows.forEach(conflict => {
    const effect = classifyConflict(conflict);
    conflictEffects[effect] = (conflictEffects[effect] ?? 0) + 1;
  });
  return { safe, operations, images, deletions, conflicts, conflictEffects, conflictSummaryRows, scopeCountsByTable: {} };
};

const SUPPORT_ENTITY_RELATIONS: Record<string, string[]> = {
  empresa_id: ['guincho_empresas'],
  usuario_id: ['guincho_usuarios'],
  checklist_id: ['guincho_checklists'],
  template_id: ['guincho_templates_checklist'],
  campo_id: ['guincho_campos_template', 'guincho_checklist_snapshot_fields'],
  field_id: ['guincho_campos_template', 'guincho_checklist_snapshot_fields'],
  snapshot_field_id: ['guincho_checklist_snapshot_fields'],
  snapshot_id: ['guincho_checklist_snapshots'],
};

const parseSupportPayload = (value: unknown): Record<string, any> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, any>;
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch { return null; }
};

/**
 * Classifica somente para observabilidade. Não autoriza execução nem limpeza:
 * operações sem prova suficiente ficam visíveis em contagens agregadas.
 */
export const classifySupportSynchronizationSummaryScope = (
  tables: Record<string, Record<string, any>[]>,
  companyId: string,
  userId: string
): SupportScopeRows => {
  const rowsById = new Map<string, Map<string, Record<string, any>[]>>();
  Object.entries(tables).forEach(([table, rows]) => {
    const index = new Map<string, Record<string, any>[]>();
    rows.forEach(row => {
      if (typeof row.id !== 'string' || !row.id) return;
      index.set(row.id, [...(index.get(row.id) ?? []), row]);
    });
    rowsById.set(table, index);
  });

  const companyIdsForRow = (table: string, row: Record<string, any>, visited = new Set<string>()): Set<string> => {
    const identity = `${table}:${String(row.id ?? '')}`;
    if (visited.has(identity)) return new Set();
    visited.add(identity);
    const companies = new Set<string>();
    if (typeof row.empresa_id === 'string' && row.empresa_id) companies.add(row.empresa_id);
    if (table === APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME && typeof row.id === 'string') companies.add(row.id);
    Object.entries(SUPPORT_ENTITY_RELATIONS).forEach(([field, targetTables]) => {
      const parentId = row[field];
      if (typeof parentId !== 'string' || !parentId) return;
      targetTables.forEach(targetTable => {
        (rowsById.get(targetTable)?.get(parentId) ?? []).forEach(parent => {
          companyIdsForRow(targetTable, parent, visited).forEach(id => companies.add(id));
        });
      });
    });
    return companies;
  };

  const payloadEvidence = (payload: Record<string, any> | null): { direct: Set<string>; references: Set<string> } => {
    const direct = new Set<string>();
    const references = new Set<string>();
    if (!payload) return { direct, references };
    const visit = (value: unknown): void => {
      if (!value || typeof value !== 'object') return;
      if (Array.isArray(value)) { value.forEach(visit); return; }
      const record = value as Record<string, unknown>;
      if (typeof record.empresa_id === 'string' && record.empresa_id) direct.add(record.empresa_id);
      Object.entries(SUPPORT_ENTITY_RELATIONS).forEach(([field, targetTables]) => {
        const parentId = record[field];
        if (typeof parentId !== 'string' || !parentId) return;
        targetTables.forEach(targetTable => {
          (rowsById.get(targetTable)?.get(parentId) ?? []).forEach(parent => {
            companyIdsForRow(targetTable, parent).forEach(id => references.add(id));
          });
        });
      });
      Object.values(record).forEach(visit);
    };
    visit(payload);
    return { direct, references };
  };

  const operations = tables.pending_operations ?? [];
  const payloads = operations.map(row => parseSupportPayload(row.data));
  const canonicalCollection = (collection: unknown): string => {
    const name = String(collection ?? '');
    const configured = Object.entries(APP_CONSTANTS.DATABASE.TABLES).find(([key, value]) =>
      value.NAME === name || key.toLowerCase() === name.toLowerCase()
    );
    return configured?.[1].NAME ?? name;
  };
  const entityCompanyIds = operations.map(row => {
    const collection = canonicalCollection(row.collection);
    const itemId = String(row.item_id ?? row.itemId ?? '');
    return (rowsById.get(collection)?.get(itemId) ?? []).reduce<Set<string>>((all, entity) => {
      companyIdsForRow(collection, entity).forEach(id => all.add(id));
      return all;
    }, new Set());
  });
  const payloadEvidenceByOperation = payloads.map(payloadEvidence);
  const payloadCompanyIds = payloadEvidenceByOperation.map(evidence => new Set([...evidence.direct, ...evidence.references]));
  const actorCompanyIds = operations.map(row => {
    const actorId = String(row.user_id ?? '');
    return (rowsById.get('guincho_usuarios')?.get(actorId) ?? []).reduce<Set<string>>((all, actor) => {
      companyIdsForRow('guincho_usuarios', actor).forEach(id => all.add(id));
      return all;
    }, new Set());
  });

  const pairGroups = new Map<string, number[]>();
  operations.forEach((row, index) => {
    const collection = canonicalCollection(row.collection);
    const itemId = String(row.item_id ?? row.itemId ?? '');
    if (!collection || !itemId) return;
    const key = `${collection}:${itemId}`;
    pairGroups.set(key, [...(pairGroups.get(key) ?? []), index]);
  });
  const pairedCurrentTenant = operations.map((row, index) => {
    const operation = String(row.operation ?? '').toLowerCase();
    if (operation !== 'create' && operation !== 'delete' && operation !== 'checklist_delete') return false;
    const collection = String(row.collection ?? '');
    const itemId = String(row.item_id ?? row.itemId ?? '');
    const peers = pairGroups.get(`${collection}:${itemId}`) ?? [];
    return peers.some(peerIndex => {
      if (peerIndex === index) return false;
      const peerOperation = String(operations[peerIndex].operation ?? '').toLowerCase();
      const isCreateDeletePair = (operation === 'create' && ['delete', 'checklist_delete'].includes(peerOperation)) ||
        (['delete', 'checklist_delete'].includes(operation) && peerOperation === 'create');
      if (!isCreateDeletePair) return false;
      const own = new Set([...entityCompanyIds[index], ...payloadCompanyIds[index]]);
      const peer = new Set([...entityCompanyIds[peerIndex], ...payloadCompanyIds[peerIndex]]);
      const ownDirect = payloadEvidenceByOperation[index].direct.has(companyId) || entityCompanyIds[index].has(companyId);
      const peerDirect = payloadEvidenceByOperation[peerIndex].direct.has(companyId) || entityCompanyIds[peerIndex].has(companyId);
      return ownDirect && peerDirect && own.has(companyId) && peer.has(companyId) &&
        ![...own, ...peer].some(id => id !== companyId);
    });
  });

  const includedOperations: Record<string, any>[] = [];
  const scopeCountsByTable: SupportScopeCountsByTable = {};
  const addScopeCount = (table: string, reason: SupportScopeReason): void => {
    scopeCountsByTable[table] ??= {};
    scopeCountsByTable[table][reason] = (scopeCountsByTable[table][reason] ?? 0) + 1;
  };
  operations.forEach((operation, index) => {
    const ownEvidence = new Set([...payloadCompanyIds[index], ...entityCompanyIds[index]]);
    const allEvidence = new Set([...ownEvidence, ...actorCompanyIds[index]]);
    const hasForeignOwnEvidence = [...ownEvidence].some(id => id !== companyId);
    const hasCurrentOwnEvidence = ownEvidence.has(companyId);
    const hasCurrentActorEvidence = actorCompanyIds[index].has(companyId);
    const hasForeignActorEvidence = [...actorCompanyIds[index]].some(id => id !== companyId);
    const hasContradiction = (hasCurrentOwnEvidence && (hasForeignOwnEvidence || hasForeignActorEvidence)) ||
      (hasForeignOwnEvidence && hasCurrentActorEvidence);
    if (hasContradiction) { addScopeCount('pending_operations', 'contradictory_links'); return; }
    if (hasForeignOwnEvidence) { addScopeCount('pending_operations', 'other_company'); return; }
    const hasEntityProof = entityCompanyIds[index].has(companyId);
    const hasPayloadAndReferenceProof = payloadEvidenceByOperation[index].direct.has(companyId) &&
      payloadEvidenceByOperation[index].references.has(companyId);
    const hasPairedProof = pairedCurrentTenant[index];
    if (allEvidence.has(companyId) && (hasEntityProof || hasPayloadAndReferenceProof ||
      (payloadEvidenceByOperation[index].direct.has(companyId) && hasPairedProof))) {
      includedOperations.push(operation);
      return;
    }
    addScopeCount('pending_operations', 'unlinked');
  });

  const knownCompanyIds = new Set<string>([companyId]);
  Object.entries(tables).forEach(([table, rows]) => rows.forEach(row => {
    if (typeof row.empresa_id === 'string' && row.empresa_id) knownCompanyIds.add(row.empresa_id);
    if (table === APP_CONSTANTS.DATABASE.TABLES.EMPRESAS.NAME && typeof row.id === 'string') knownCompanyIds.add(row.id);
  }));
  const pathCompanyIds = (path: unknown): Set<string> => {
    const ids = new Set<string>();
    String(path ?? '').split(/[\\/]+/).forEach(segment => {
      if (knownCompanyIds.has(segment)) ids.add(segment);
    });
    return ids;
  };
  const classifyRelatedQueueRow = (table: string, row: Record<string, any>): { reason?: SupportScopeReason; included: boolean } => {
    const evidence = new Set<string>();
    if (typeof row.empresa_id === 'string' && row.empresa_id) evidence.add(row.empresa_id);
    const itemId = String(row.item_id ?? row.itemId ?? row.campo_id ?? '');
    if (itemId) Object.entries(tables).forEach(([entityTable, rows]) => {
      (rowsById.get(entityTable)?.get(itemId) ?? []).forEach(entity => {
        companyIdsForRow(entityTable, entity).forEach(id => evidence.add(id));
      });
    });
    pathCompanyIds(row.path).forEach(id => evidence.add(id));
    const hasCurrent = evidence.has(companyId);
    const hasForeign = [...evidence].some(id => id !== companyId);
    if (hasCurrent && hasForeign) return { included: false, reason: 'contradictory_links' };
    if (hasForeign) return { included: false, reason: 'other_company' };
    if (hasCurrent) return { included: true };
    return { included: false, reason: 'unlinked' };
  };
  const classifyQueueTable = (table: string): Record<string, any>[] => (tables[table] ?? []).filter(row => {
    const result = classifyRelatedQueueRow(table, row);
    if (!result.included && result.reason) addScopeCount(table, result.reason);
    return result.included;
  });
  const images = classifyQueueTable('pending_images');
  const deletions = classifyQueueTable('pending_deletions');
  const conflicts = (tables.sync_conflicts ?? []).filter(row => {
    const entityCompanyIds = new Set<string>();
    if (typeof row.empresa_id === 'string' && row.empresa_id) entityCompanyIds.add(row.empresa_id);
    const authorId = String(row.user_id ?? '');
    (rowsById.get('guincho_usuarios')?.get(authorId) ?? []).forEach(actor => {
      companyIdsForRow('guincho_usuarios', actor).forEach(id => entityCompanyIds.add(id));
    });
    const hasCurrent = entityCompanyIds.has(companyId);
    const hasForeign = [...entityCompanyIds].some(id => id !== companyId);
    if (hasCurrent && hasForeign) { addScopeCount('sync_conflicts', 'contradictory_links'); return false; }
    if (hasForeign) { addScopeCount('sync_conflicts', 'other_company'); return false; }
    if (hasCurrent) return true;
    addScopeCount('sync_conflicts', 'unlinked');
    return false;
  });
  const conflictSummaryRows = conflicts.filter(row => !['resolved', 'ignored'].includes(String(row.status ?? '').toLowerCase()));
  const conflictEffects: Record<string, number> = {};
  const classifyConflict = createSupportConflictClassifier(tables);
  conflictSummaryRows.forEach(conflict => {
    const effect = classifyConflict(conflict);
    conflictEffects[effect] = (conflictEffects[effect] ?? 0) + 1;
  });
  return {
    operations: includedOperations, images, deletions, conflicts: conflicts.filter(isActiveSyncConflict),
    conflictEffects, conflictSummaryRows, scopeCountsByTable,
  };
};

// Importar SQLiteAdapter apenas em ambientes não-web
// Esta é uma forma de evitar o erro na web sem usar importações dinâmicas
let SQLiteAdapter: any = null;
if (Platform.OS !== 'web') {
  // Esta importação só será executada em ambiente mobile
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  SQLiteAdapter = require('../adapters/SQLiteAdapter').default;
}

// Loggers específicos por método
const mainLogger = DatabaseServiceLogger.Main;
const initLogger = DatabaseServiceLogger.Initialize;
const createItemLogger = DatabaseServiceLogger.CreateItem;
const readItemLogger = DatabaseServiceLogger.ReadItem;
const updateItemLogger = DatabaseServiceLogger.UpdateItem;
const deleteItemLogger = DatabaseServiceLogger.DeleteItem;
const listItemsLogger = DatabaseServiceLogger.ListItems;
const getPendingOpsLogger = DatabaseServiceLogger.GetPendingOps;
const removePendingOpLogger = DatabaseServiceLogger.RemovePendingOp;
const closeLogger = DatabaseServiceLogger.Close;
const validateInitLogger = DatabaseServiceLogger.ValidateInit;
const bulkUpsertLogger = DatabaseServiceLogger.BulkUpsert;
const bulkUpsertChecklistValuesLogger = DatabaseServiceLogger.BulkUpsertChecklistValues;

const containsLocalChecklistValue = (value: string): boolean => {
  if (!value) return false;
  return (
    value.includes('checklist-') ||
    value.startsWith('file://') ||
    value.startsWith('blob:')
  );
};

/**
 * Serviço de banco de dados que gerencia o adaptador correto para cada plataforma
 */
class DatabaseService {
  private adapter: IDatabaseAdapter | null = null;
  private initialized: boolean = false;
  private initializingPromise: Promise<void> | null = null;
  private mediaMutationsInFlight = 0;
  private destructiveResetInProgress = false;

  /**
   * Inicializa o banco de dados com o adaptador correto para a plataforma
   * @param userId ID do usuário atual (opcional)
   */
  private restartRequired = false;

  async initialize(userId?: string): Promise<void> {
    if (this.restartRequired) throw new Error('database_reset_restart_required');
    if (this.initialized) {
      if (userId) {
        initLogger.info('[Init] DatabaseService já inicializado – atualizando userId para: ' + userId);
        this.adapter!.setUserId(userId);
      } else {
        initLogger.warn('[Init] DatabaseService já inicializado – sem userId para atualizar');
      }
      return;
    }

    if (this.initializingPromise) {
      initLogger.info('[Init] Inicialização do DatabaseService já está em andamento. Aguardando conclusão.');
      await this.initializingPromise;

      // Se a chamada concorrente trouxer um userId, atualiza no adaptador após o init concluir.
      if (userId && this.adapter) {
        initLogger.info('[Init] Inicialização concluída enquanto aguardava – atualizando userId para: ' + userId);
        this.adapter.setUserId(userId);
      }
      return;
    }

    const currentInitialization = this.performInitialize(userId);
    this.initializingPromise = currentInitialization;

    try {
      await currentInitialization;
    } finally {
      if (this.initializingPromise === currentInitialization) {
        this.initializingPromise = null;
      }
    }
  }

  /**
   * Executa o fluxo real de inicialização do adaptador.
   * Mantém o commit de `this.adapter` atômico para evitar corrida entre chamadas paralelas.
   */
  private async performInitialize(userId?: string): Promise<void> {
    //initLogger.info('Inicializando DatabaseService...');

    let nextAdapter: IDatabaseAdapter | null = null;

    try {
      if (Platform.OS === 'web') {
        initLogger.info('Plataforma web detectada, usando DexieAdapter');
        nextAdapter = new DexieAdapter();
      } else {
        // Para mobile, usar SQLiteAdapter
        initLogger.info('Plataforma mobile detectada, usando SQLiteAdapter');

        if (!SQLiteAdapter) {
          const errorMsg = 'Erro crítico: SQLiteAdapter não está disponível em ambiente mobile';
          initLogger.error(errorMsg);
          databaseActions.setError(errorMsg);
          throw new Error(errorMsg);
        }

        // Adicionar a asserção de não-nulo para satisfazer o TypeScript
        nextAdapter = new (SQLiteAdapter as any)();
      }

      if (!nextAdapter) {
        throw new Error('Adapter de banco de dados não foi inicializado.');
      }

      await nextAdapter.initialize(userId);

      // Commit atômico após init concluído
      this.adapter = nextAdapter;
      this.initialized = true;
    } catch (error) {
      initLogger.error('Erro ao inicializar DatabaseService:', error);
      databaseActions.setError(`Erro ao inicializar banco de dados: ${error}`);

      this.initialized = false;
      this.adapter = null;

      if (nextAdapter) {
        try {
          await nextAdapter.close();
        } catch (closeError) {
          initLogger.warn('Falha ao fechar adaptador após erro de inicialização:', closeError);
        }
      }

      throw error;
    }
  }

  /**
   * Verifica se o serviço está inicializado
   */
  private validateInitialized(): void {
    if (this.restartRequired) throw new Error('database_reset_restart_required');
    if (!this.initialized || !this.adapter) {
      validateInitLogger.error('DatabaseService não inicializado');
      throw new Error('DatabaseService não inicializado. Chame initialize() primeiro.');
    }
  }

  /**
   * Converte a chave ou nome da tabela para o nome correto da tabela
   * @param collection A chave em APP_CONSTANTS.DATABASE.TABLES ou o nome exato da tabela 
   * @returns O nome exato da tabela
   * @throws Error se a collection não for válida
   */
  private getTableName(collection: string): string {
    const raw = collection.trim();

    // 1) Aceita qualquer nome declarado no contrato local.
    const lower = raw.toLowerCase();
    const tableEntry = Object.entries(APP_CONSTANTS.DATABASE.TABLES).find(
      ([_, definition]) => definition.NAME.toLowerCase() === lower
    );
    if (tableEntry) {
      return tableEntry[1].NAME;
    }

    // 2) Se é chave em TABLES (case-insensitive)
    const key = raw.toUpperCase();
    const tableDef = APP_CONSTANTS.DATABASE.TABLES[key as keyof typeof APP_CONSTANTS.DATABASE.TABLES];
    if (tableDef) {
      return tableDef.NAME;
    }

    // 3) Falha rápida: entrada inválida
    throw new Error(`Collection inválida: "${collection}". Use o nome da tabela (ex: "guincho_users") ou a chave (ex: "USERS").`);
  }

  /**
   * Cria um novo item na coleção especificada
   * @param collection Nome da coleção ou chave em APP_CONSTANTS.DATABASE.TABLES
   * @param item Item a ser criado
   * @param options Define a origem da mutação e se a operação deve ir para a outbox
   * @returns ID do item criado
   */
  async createItem<T>(collection: string, item: T, options: DatabaseMutationInput = false): Promise<string> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    const sanitized = sanitizeLocalTablePayload(tableName, item as any);
    if (sanitized.droppedKeys.length > 0) {
      createItemLogger.debug(`Campos fora do schema local ignorados em createItem para "${tableName}": ${sanitized.droppedKeys.join(', ')}`);
    }
    const skipPendingOperation = typeof options === 'boolean'
      ? options
      : options.skipPendingOperation === true;
    createItemLogger.info(`Criando item na coleção "${tableName}"${skipPendingOperation ? ' (sem registrar pendência)' : ''}`);
    const id = await this.adapter!.createItem<T>(tableName, sanitized.data as T, options);
    return id;
  }

  /**
   * Lê um item específico de uma coleção
   * @param collection Nome da coleção ou chave em APP_CONSTANTS.DATABASE.TABLES
   * @param id ID do item
   * @returns Item encontrado ou null
   */
  async readItem<T>(collection: string, id: string): Promise<T | null> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    //readItemLogger.info(`Lendo item da coleção "${tableName}" com ID "${id}"`);
    const item = await this.adapter!.readItem<T>(tableName, id);
    //readItemLogger.info(`Item ${item ? 'encontrado' : 'não encontrado'} na coleção "${tableName}" com ID "${id}"`);
    return item;
  }

  /**
   * Atualiza um item existente em uma coleção
   * @param collection Nome da coleção ou chave em APP_CONSTANTS.DATABASE.TABLES
   * @param id ID do item
   * @param updates Atualizações parciais
   * @param options Define a origem da mutação e se a operação deve ir para a outbox
   * @returns Boolean indicando sucesso
   */
  async updateItem<T>(collection: string, id: string, updates: Partial<T>, options: DatabaseMutationInput = false): Promise<boolean> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    const sanitized = sanitizeLocalTablePayload(tableName, updates as any);
    if (sanitized.droppedKeys.length > 0) {
      updateItemLogger.debug(`Campos fora do schema local ignorados em updateItem para "${tableName}": ${sanitized.droppedKeys.join(', ')}`);
    }
    if (Object.keys(sanitized.data ?? {}).length === 0) {
      updateItemLogger.warn(`Update ignorado para "${tableName}/${id}" porque nenhum campo existe no schema local.`);
      return false;
    }
    const skipPendingOperation = typeof options === 'boolean'
      ? options
      : options.skipPendingOperation === true;
    updateItemLogger.info(`Atualizando item na coleção "${tableName}" com ID "${id}"${skipPendingOperation ? ' (sem registrar pendência)' : ''}`);
    const success = await this.adapter!.updateItem<T>(tableName, id, sanitized.data as Partial<T>, options);
    updateItemLogger.info(`Atualização ${success ? 'bem-sucedida' : 'falhou'} para o item na coleção "${tableName}" com ID "${id}"`);
    return success;
  }

  async updateItemIfUnchanged(
    collection: string,
    id: string,
    updates: Record<string, unknown>,
    expected: Record<string, unknown>,
    skipPendingOperation: boolean = false,
    canAcknowledge?: () => boolean,
    guard?: HistoricalInsertGuard
  ): Promise<boolean> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    const sanitizedUpdates = sanitizeLocalTablePayload(tableName, updates).data;
    const sanitizedExpected = sanitizeLocalTablePayload(tableName, expected).data;
    if (Object.keys(sanitizedUpdates ?? {}).length === 0) {
      throw new Error(`Atualizacao comparada sem campos validos para "${tableName}/${id}".`);
    }
    return this.adapter!.updateItemIfUnchanged(
      tableName,
      id,
      sanitizedUpdates,
      sanitizedExpected,
      skipPendingOperation,
      canAcknowledge,
      guard
    );
  }

  /**
   * Atualiza um registro estrutural e registra o tombstone pendente no mesmo commit local.
   */
  async retireItemAndPending(
    collection: string,
    id: string,
    updates: Record<string, unknown>,
    pendingData: Record<string, unknown>
  ): Promise<boolean> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    const sanitized = sanitizeLocalTablePayload(tableName, updates);
    if (Object.keys(sanitized.data ?? {}).length === 0) {
      throw new Error(`Aposentadoria sem campos validos para "${tableName}/${id}".`);
    }

    const adapter = this.adapter!;
    const origin = capturePendingWorkOrigin();
    if (!origin || this.destructiveResetInProgress) throw new Error('structural_origin_required');
    const canCommit = () => { const current = capturePendingWorkOrigin(); return this.adapter === adapter && !this.destructiveResetInProgress && current?.userId === origin.userId && current?.empresaId === origin.empresaId; };
    const baseline = origin && tableName === 'guincho_checklists' ? readPartnerChecklistBaseline(origin.empresaId, origin.userId, id) : null;
    const deletionPayload = baseline && (baseline.enabled || baseline.touched)
      ? { ...pendingData, expected_partner_revision: baseline.revision } : pendingData;
    const success = await adapter.retireItemAndPending(
      tableName,
      id,
      sanitized.data,
      deletionPayload, { origin, canCommit }
    );
    updateItemLogger.info(
      `Aposentadoria ${success ? 'bem-sucedida' : 'falhou'} para o item "${tableName}/${id}"`
    );
    return success;
  }

  /** Persiste versao, campos estruturais e suas pendencias no mesmo commit. */
  async applyChecklistStructuralMutation(
    input: ChecklistStructuralMutationInput
  ): Promise<ChecklistStructuralMutationResult> {
    this.validateInitialized();
    const adapter = this.adapter!;
    const origin = capturePendingWorkOrigin();
    if (!origin || this.destructiveResetInProgress) throw new Error('structural_origin_required');
    const canCommit = () => { const current = capturePendingWorkOrigin(); return this.adapter === adapter && !this.destructiveResetInProgress && current?.userId === origin.userId && current?.empresaId === origin.empresaId; };
    const templateCollection = this.getTableName(APP_CONSTANTS.DATABASE.TABLES.TEMPLATES_CHECKLIST.NAME);
    const fieldCollection = this.getTableName(APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CAMPOS_TEMPLATE.NAME);
    const fieldMutations: ChecklistStructuralFieldMutation[] = input.fieldMutations.map(mutation => {
      if (mutation.operation === 'create') {
        const sanitized = sanitizeLocalTablePayload(fieldCollection, mutation.record);
        return { operation: 'create', record: sanitized.data };
      }

      const sanitized = sanitizeLocalTablePayload(fieldCollection, mutation.updates);
      return mutation.operation === 'delete'
        ? {
            operation: 'delete',
            id: mutation.id,
            updates: sanitized.data,
            pendingData: mutation.pendingData,
          }
        : {
            operation: 'update',
            id: mutation.id,
            updates: sanitized.data,
          };
    });

    return adapter.applyChecklistStructuralMutation({
      guard: { origin, canCommit },
      templateId: input.templateId,
      fieldMutations,
    });
  }

  /**
   * Remove um item de uma coleção
   * @param collection Nome da coleção ou chave em APP_CONSTANTS.DATABASE.TABLES
   * @param id ID do item
   * @param options Define a origem da mutação e se a operação deve ir para a outbox
   * @returns Boolean indicando sucesso
   */
  async deleteItem(collection: string, id: string, options: DatabaseMutationInput = false): Promise<boolean> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    const skipPendingOperation = typeof options === 'boolean'
      ? options
      : options.skipPendingOperation === true;
    deleteItemLogger.info(`Removendo item da coleção "${tableName}" com ID "${id}"${skipPendingOperation ? ' (sem registrar pendência)' : ''}`);
    let deletionOptions = options;
    if (!skipPendingOperation && (tableName === 'guincho_checklists' || tableName === 'guincho_valores_checklist')) {
      const origin = capturePendingWorkOrigin();
      const row = await this.adapter!.readItem<any>(tableName, id);
      const checklistId = tableName === 'guincho_checklists' ? id : row?.checklist_id;
      const baseline = origin && checklistId ? readPartnerChecklistBaseline(origin.empresaId, origin.userId, checklistId) : null;
      if (baseline && (baseline.enabled || baseline.touched)) {
        deletionOptions = { ...(typeof options === 'object' ? options : {}), expectedPartnerRevision: baseline.revision };
      }
    }
    const success = await this.adapter!.deleteItem(tableName, id, deletionOptions);
    deleteItemLogger.info(`Remoção ${success ? 'bem-sucedida' : 'falhou'} para o item na coleção "${tableName}" com ID "${id}"`);
    return success;
  }

  async retainTombstonedChecklistUpload(pending: Record<string, any>, companyId: string, canCommit: () => boolean): Promise<boolean> {
    this.validateInitialized();
    const adapter = this.adapter!;
    return adapter.retainTombstonedChecklistUpload({ pending, companyId, canCommit: () => this.adapter === adapter && !this.restartRequired && canCommit() });
  }

  async deleteChecklistPreservingMedia(checklistId: string): Promise<boolean> {
    this.validateInitialized();
    const adapter = this.adapter!;
    const origin = capturePendingWorkOrigin();
    if (!origin || this.destructiveResetInProgress) throw new Error('checklist_deletion_origin_required');
    const baseline = readPartnerChecklistBaseline(origin.empresaId, origin.userId, checklistId);
    this.mediaMutationsInFlight++;
    try {
      return await adapter.deleteChecklistPreservingMedia({ checklistId, origin, intentId: `checklist:${origin.empresaId}:${origin.userId}:${checklistId}`,
        ...(baseline && (baseline.enabled || baseline.touched) ? { expectedPartnerRevision: baseline.revision } : {}),
        canCommit: () => { const current = capturePendingWorkOrigin(); return this.adapter === adapter &&
          current?.userId === origin.userId && current?.empresaId === origin.empresaId; },
      });
    } finally { this.mediaMutationsInFlight--; }
  }

  async deleteItemIfUnchanged(
    collection: string,
    id: string,
    expected: Record<string, unknown>,
    skipPendingOperation: boolean = false,
    canAcknowledge?: () => boolean
  ): Promise<boolean> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    const sanitizedExpected = sanitizeLocalTablePayload(tableName, expected).data;
    return this.adapter!.deleteItemIfUnchanged(tableName, id, sanitizedExpected, skipPendingOperation, canAcknowledge);
  }

  /**
   * Lista itens de uma coleção, opcionalmente com filtros
   * @param collection Nome da coleção ou chave em APP_CONSTANTS.DATABASE.TABLES
   * @param query Objeto de consulta para filtrar os resultados
   * @returns Array de itens encontrados
   */
  async listItemsByIdPage<T>(collection: string, afterId: string, limit: number, metadata?: CanonicalMetadataCandidateScope): Promise<T[]> {
    this.validateInitialized();
    return this.adapter!.listItemsByIdPage<T>(this.getTableName(collection), afterId, limit, metadata);
  }

  async listItems<T>(collection: string, query?: any): Promise<T[]> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    listItemsLogger.info(`Listando itens da coleção "${tableName}"${query ? ' com filtros' : ''}`);
    const items = await this.adapter!.listItems<T>(tableName, query);
    listItemsLogger.info(`Encontrados ${items.length} itens na coleção "${tableName}"`);
    return items;
  }

  async captureRecoverySnapshot(
    input: RecoveryCaptureInput
  ): Promise<RecoveryConsistentSnapshot> {
    this.validateInitialized();
    return this.adapter!.captureRecoverySnapshot(input);
  }

  async captureFullStateSnapshot(companyId: string, userId: string): Promise<SupportFullStateSnapshot> {
    this.validateInitialized();
    return this.adapter!.captureFullStateSnapshot(companyId, userId);
  }

  /**
   * Busca operações pendentes para sincronização
   * @returns Array de operações pendentes
   */
  async getPendingOperations(): Promise<PendingOperation[]> {
    this.validateInitialized();
    getPendingOpsLogger.info('Buscando operações pendentes');
    const rawOps = await this.adapter!.getPendingOperations() as PendingOperationRaw[];
    getPendingOpsLogger.info(`Encontradas ${rawOps.length} operações pendentes`);
    // Mapear PendingOperationRaw para PendingOperation
    return rawOps.map(op => ({
      id: op.id,
      operation: op.operation,
      collection: op.collection,
      itemId: op.item_id,
      data: op.data,
      timestamp: op.timestamp,
      user_id: op.user_id ?? null,
      empresa_id: op.empresa_id ?? null,
    }));
  }

  private async readSupportScopeTables(): Promise<Record<string, Record<string, any>[]>> {
    const tables: Record<string, Record<string, any>[]> = {};
    for (const table of Object.values(APP_CONSTANTS.DATABASE.TABLES).map(value => value.NAME)) {
      tables[table] = await this.listItems<Record<string, any>>(table);
    }
    return tables;
  }

  async getSupportSynchronizationSummaryRows(companyId: string, userId: string): Promise<SupportScopeRows> {
    const tables = await this.readSupportScopeTables();
    return classifySupportSynchronizationSummaryScope(tables, companyId, userId);
  }

  async getSupportResetScopeRows(companyId: string, userId: string): Promise<SupportScopeRows> {
    const tables = await this.readSupportScopeTables();
    const scoped = classifySupportLocalScope(tables, companyId, userId);
    if (!scoped.safe) throw new Error('support_local_scope_ambiguous');
    return scoped;
  }

  /**
   * Remove uma operação pendente após sincronização bem-sucedida
   * @param id ID da operação pendente
   * @returns Boolean indicando sucesso
   */
  async removePendingOperation(id: string): Promise<boolean> {
    this.validateInitialized();
    removePendingOpLogger.info(`Removendo operação pendente com ID "${id}"`);
    const success = await this.adapter!.removePendingOperation(id);
    removePendingOpLogger.info(`Remoção ${success ? 'bem-sucedida' : 'falhou'} para operação pendente com ID "${id}"`);
    return success;
  }

  async removePendingOperationIfUnchanged(
    id: string,
    expected: Pick<PendingOperationRaw, 'operation' | 'collection' | 'item_id' | 'data' | 'timestamp' | 'user_id' | 'empresa_id'>,
    canAcknowledge?: () => boolean
  ): Promise<boolean> {
    this.validateInitialized();
    removePendingOpLogger.info(`Removendo operação pendente com ID "${id}" se ainda estiver inalterada`);
    const success = await this.adapter!.removePendingOperationIfUnchanged(id, expected, canAcknowledge);
    removePendingOpLogger.info(`Remoção condicional ${success ? 'bem-sucedida' : 'não aplicada'} para operação pendente com ID "${id}"`);
    return success;
  }

  /**
   * Realiza upsert local em lote usando o adapter da plataforma.
   * Mantém o caminho centralizado para que o SyncService não precise conhecer o adapter concreto.
   */
  async bulkUpsert<T>(collection: string, rows: T[], skipPendingOperation: boolean = true): Promise<void> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    const sanitized = sanitizeLocalTableRows(tableName, rows as any[]);
    if (sanitized.droppedKeys.length > 0) {
      bulkUpsertLogger.debug(`Campos fora do schema local ignorados em bulkUpsert para "${tableName}": ${sanitized.droppedKeys.join(', ')}`);
    }

    const rowsForAdapter = sanitized.rows as T[];
    const sampleIds = rowsForAdapter
      .slice(0, 3)
      .map((row: any) => row?.id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);

    bulkUpsertLogger.info(
      `Upsert em lote na coleção "${tableName}" com ${rowsForAdapter.length} registro(s)` +
      `${skipPendingOperation ? ' (sem registrar pendências)' : ''}` +
      `${sampleIds.length > 0 ? ` | amostra IDs: ${sampleIds.join(', ')}` : ''}`
    );

    await this.adapter!.bulkUpsert(tableName, rowsForAdapter as any[], skipPendingOperation);
  }

  /**
   * Insere somente linhas ausentes. Este caminho não cria pendências e nunca
   * substitui uma linha local que tenha surgido durante o pull.
   */
  async bulkInsertIfAbsent<T>(collection: string, rows: T[], guard?: HistoricalInsertGuard): Promise<number> {
    this.validateInitialized();
    const tableName = this.getTableName(collection);
    const sanitized = sanitizeLocalTableRows(tableName, rows as any[]);
    if (sanitized.droppedKeys.length > 0) {
      bulkUpsertLogger.debug(
        `Campos fora do schema local ignorados em bulkInsertIfAbsent para "${tableName}": ${sanitized.droppedKeys.join(', ')}`
      );
    }

    return guard
      ? this.adapter!.bulkInsertIfAbsent(tableName, sanitized.rows, guard)
      : this.adapter!.bulkInsertIfAbsent(tableName, sanitized.rows);
  }

  /**
   * Persiste o agregado checklist+snapshot em uma transacao do adapter.
   * Nenhuma parte e exposta ao sync sem que a pendencia create exista junto.
   */
  async createChecklistSnapshotAndPending(input: ChecklistSnapshotAtomicCreateInput): Promise<void> {
    this.validateInitialized();

    const checklistCollection = this.getTableName(input.checklist.collection);
    const schemaVersionCollection = input.schemaVersion
      ? this.getTableName(input.schemaVersion.collection)
      : null;
    const snapshotCollection = this.getTableName(input.snapshot.collection);
    const snapshotFieldsCollection = this.getTableName(input.snapshotFields.collection);
    const checklist = sanitizeLocalTablePayload(checklistCollection, input.checklist.record).data;
    const schemaVersion = input.schemaVersion
      ? sanitizeLocalTablePayload(schemaVersionCollection!, input.schemaVersion.record).data
      : null;
    const snapshot = sanitizeLocalTablePayload(snapshotCollection, input.snapshot.record).data;
    const snapshotFields = sanitizeLocalTableRows(
      snapshotFieldsCollection,
      input.snapshotFields.records
    ).rows;

    if (snapshotFields.length === 0) {
      throw new Error('Criacao atomica de checklist exige ao menos um campo de snapshot.');
    }

    await this.adapter!.createChecklistSnapshotAndPending({
      checklist: { collection: checklistCollection, record: checklist },
      ...(schemaVersionCollection && schemaVersion
        ? { schemaVersion: { collection: schemaVersionCollection, record: schemaVersion } }
        : {}),
      snapshot: { collection: snapshotCollection, record: snapshot },
      snapshotFields: { collection: snapshotFieldsCollection, records: snapshotFields },
      pendingOperation: {
        ...input.pendingOperation,
        collection: checklistCollection,
      },
    });
  }

  /**
   * Reconciliacao atomica do schema provisorio com a resposta canonica do RPC.
   * A fila de pendencias e os conflitos ficam fora do conjunto mutado.
   */
  async reconcileChecklistSnapshotCanonical(
    input: ChecklistSnapshotCanonicalReconciliationInput
  ): Promise<void> {
    this.validateInitialized();

    const schemaVersionCollection = this.getTableName(input.schemaVersion.collection);
    const checklistCollection = this.getTableName(input.checklist.collection);
    const snapshotCollection = this.getTableName(input.snapshot.collection);
    const snapshotFieldsCollection = this.getTableName(input.snapshotFields.collection);
    const schemaVersion = sanitizeLocalTablePayload(
      schemaVersionCollection,
      input.schemaVersion.record
    ).data;
    const checklist = sanitizeLocalTablePayload(checklistCollection, input.checklist.record).data;
    const expectedChecklist = input.expectedChecklist
      ? sanitizeLocalTablePayload(checklistCollection, input.expectedChecklist).data
      : undefined;
    const snapshot = sanitizeLocalTablePayload(snapshotCollection, input.snapshot.record).data;
    const snapshotFields = sanitizeLocalTableRows(
      snapshotFieldsCollection,
      input.snapshotFields.records
    ).rows;

    if (snapshotFields.length === 0) {
      throw new Error('Reconciliacao atomica de checklist exige ao menos um campo de snapshot.');
    }

    const adapter = this.adapter!.reconcileChecklistSnapshotCanonical;
    if (!adapter) {
      throw new Error('Adapter local sem suporte a reconciliacao atomica de snapshot.');
    }

    await adapter.call(this.adapter!, {
      provisionalSchemaVersionId: input.provisionalSchemaVersionId,
      schemaVersion: { collection: schemaVersionCollection, record: schemaVersion },
      ...(expectedChecklist ? { expectedChecklist } : {}),
      checklist: { collection: checklistCollection, record: checklist },
      snapshot: { collection: snapshotCollection, record: snapshot },
      snapshotFields: { collection: snapshotFieldsCollection, records: snapshotFields },
    });
  }

  /**
   * Registra uma operação pendente para sincronização posterior
   * @param operation Tipo de operação
   * @param collection Nome da coleção/tabela
   * @param itemId ID do item afetado
   * @param data Dados relacionados à operação (opcional)
   * @returns ID da operação pendente
   */
  async registerPendingOperation(
    operation: 'create' | 'update' | 'delete' | 'checklist_partial_update',
    collection: string,
    itemId: string,
    data?: any
  ): Promise<string> {
    this.validateInitialized();
    mainLogger.info(`Registrando operação pendente: ${operation} para ${collection}/${itemId}`);
    const pendingId = await this.adapter!.registerPendingOperation(operation as any, collection, itemId, data);

    return pendingId;
  }

  /**
   * Fecha a conexão com o banco de dados
   */
  async close(): Promise<void> {
    if (this.initializingPromise) {
      closeLogger.info('Aguardando inicialização em andamento antes de fechar conexão');
      try {
        await this.initializingPromise;
      } catch (initError) {
        closeLogger.warn('Inicialização falhou enquanto aguardava close:', initError);
      }
    }

    if (this.adapter) {
      closeLogger.info('Fechando conexão com o banco de dados');
      await this.adapter.close();
      this.adapter = null;
      this.initialized = false;
      closeLogger.info('Conexão com o banco de dados fechada');
    } else {
      closeLogger.warn('Tentativa de fechar banco de dados que não está inicializado');
    }
  }

  /**
   * Utilitário para limpar todas as tabelas do banco de dados (apenas para testes)
   * Exclui todas as entradas de todas as tabelas.
   */
  async clearAllData(): Promise<boolean> {
    this.validateInitialized();

    if (shouldBlockUnscopedDatabaseClear(
      IS_CHECKLIST_RECOVERY_DIAGNOSTIC,
      IS_CHECKLIST_SYNC_RECOVERY_CANARY,
      Platform.OS
    )) {
      DatabaseLogger.Main.warn('[RECOVERY] Limpeza do banco bloqueada pela quarentena');
      return false;
    }

    return this.clearAllDataInternal();
  }

  async clearAllDataForSupportReset(context: { companyId: string; userId: string; checkedAt: string }): Promise<boolean> {
    this.validateInitialized();
    const adapter = this.adapter;
    const session = globalState.user.session.get();
    const accessToken = session?.access_token;
    const sessionUserId = session?.user?.id;
    const canClear = () => {
      const user = globalState.user.user.get();
      const profile = globalState.user.profile.get();
      const current = globalState.user.session.get();
      return Boolean(accessToken && sessionUserId === context.userId &&
        current?.access_token === accessToken && current.user?.id === sessionUserId && this.adapter === adapter &&
        profile?.id === context.userId && isSupportResetAuthorizationValid({ ...context,
          currentUserId: user?.id, currentCompanyId: profile?.empresa_id,
          recoveryBlocked: isRecoveryRuntimeBlocked(),
        }));
    };
    if (!canClear()) return false;
    try {
      await this.getSupportResetScopeRows(context.companyId, context.userId);
    } catch {
      DatabaseLogger.Main.warn('[SUPPORT] Limpeza controlada rejeitada por dados locais fora do escopo.');
      return false;
    }
    if (!canClear()) return false;
    return this.clearAllDataInternal(new Set(), canClear, expected => {
      const scoped = classifySupportLocalScope(expected, context.companyId, context.userId);
      return scoped.safe && [scoped.operations, scoped.images, scoped.deletions, scoped.conflicts].every(rows => rows.length === 0);
    });
  }

  async clearAllDataForRecovery(context: RecoveryResetContext): Promise<boolean> {
    this.validateInitialized();
    const receipt = context.receipt.receipt;
    if (
      !IS_CHECKLIST_SYNC_RECOVERY_CANARY ||
      context.pendingItems.some(item => item.classification === 'unaccounted') ||
      !isRecoveryReceiptVerifiedInProcess(receipt, {
        identity: context.identity,
        recoveryId: context.recoveryId,
        manifestSha512: receipt.manifestSha512,
      })
    ) {
      DatabaseLogger.Main.warn('[RECOVERY] Limpeza autorizada rejeitada por receipt ou pendencias.');
      return false;
    }

    return this.clearAllDataInternal(new Set([
      APP_CONSTANTS.DATABASE.TABLES.ACCOUNTED_PENDING_OPERATIONS.NAME,
      APP_CONSTANTS.DATABASE.TABLES.SYNC_CONFLICTS.NAME,
    ]), undefined, expected => {
      // O executor já arquivou as filas autorizadas. Qualquer item restante exige nova conferência.
      return ['pending_operations', 'pending_deletions', 'pending_images'].every(table => !expected[table]?.length);
    });
  }

  requiresRuntimeRestart(): boolean { return this.restartRequired; }

  private async clearAllDataInternal(preserveTables: ReadonlySet<string> = new Set(), canClear?: () => boolean,
    validateExpected?: (tables: Record<string, Record<string, any>[]>) => boolean): Promise<boolean> {

    if (this.destructiveResetInProgress || this.mediaMutationsInFlight > 0) return false;
    this.destructiveResetInProgress = true;
    try {
      const adapter = this.adapter!;
      const tables = Object.values(APP_CONSTANTS.DATABASE.TABLES).map(table => table.NAME);
      const expected: Record<string, Record<string, any>[]> = {};
      for (const table of tables) expected[table] = await this.listItems(table);
      if (validateExpected && !validateExpected(expected)) return false;
      const retained = new Set(preserveTables);
      if (expected.deferred_media_cleanup?.length) {
        // O cofre permanece local; não afirmar que o protocolo de escrow o exportou.
        for (const table of ['deferred_media_cleanup', 'checklist_photos', 'checklist_signatures',
          'checklist_marcacoes', 'checklist_marcacao_bkg_images']) retained.add(table);
      }
      const cleared = await adapter.clearTablesIfUnchanged(expected, [...retained], () => this.adapter === adapter && (!canClear || canClear()));
      if (cleared) this.restartRequired = true;
      return cleared;
    } catch (error) {
      DatabaseLogger.Main.error('Erro ao limpar todas as tabelas:', error);
      return false;
    } finally {
      this.destructiveResetInProgress = false;
    }
  }

  async commitArchivedPartialReplay(input: ArchivedReplayCommit): Promise<boolean> {
    this.validateInitialized();
    const adapter = this.adapter!;
    return adapter.commitArchivedPartialReplay({ ...input,
      canCommit: () => this.adapter === adapter && input.canCommit() });
  }

  async advanceChecklistMediaObservationBaseline(input: ChecklistMediaObservationBaselineInput): Promise<boolean> {
    this.validateInitialized();
    const adapter = this.adapter!;
    return adapter.advanceChecklistMediaObservationBaseline({ ...input,
      canAcknowledge: () => this.adapter === adapter && input.canAcknowledge() });
  }

  async adoptChecklistMediaNoteCanonical(input: ChecklistMediaCanonicalAdoptionInput): Promise<boolean> {
    this.validateInitialized();
    const adapter = this.adapter!;
    return adapter.adoptChecklistMediaNoteCanonical({ ...input,
      canCommit: () => this.adapter === adapter && input.canCommit() });
  }

  async commitChecklistMediaConflict(input: ChecklistMediaConflictCommit): Promise<boolean> {
    this.validateInitialized();
    const adapter = this.adapter!;
    return adapter.commitChecklistMediaConflict({ ...input,
      canCommit: () => this.adapter === adapter && input.canCommit() });
  }

  async finalizeChecklistMedia(input: ChecklistMediaFinalizationInput): Promise<boolean> {
    this.validateInitialized();
    return this.adapter!.finalizeChecklistMedia(input);
  }

  async completeChecklistMedia(input: ChecklistMediaCompletionInput): Promise<boolean> {
    this.validateInitialized();
    return this.adapter!.completeChecklistMedia(input);
  }

  /**
   * Atualiza em lote os valores de campos de um checklist (mais eficiente para digitação)
   * @param empresaId ID da empresa
   * @param checklistId ID do checklist
   * @param changes Objeto { [campoId]: valor } apenas com campos alterados
   * @param options Opções de configuração
   */
  async bulkUpsertChecklistValues(
    empresaId: string,
    checklistId: string,
    changes: Record<string, string>,
    options: {
      skipPendingOp?: boolean;
      mediaPending?: ChecklistMediaPendingInput;
      pendingOrigin?: PendingWorkOrigin | null;
      canAcknowledge?: () => boolean;
    } = {}
  ): Promise<void> {
    this.validateInitialized();
    const mediaMutation = !!options.mediaPending;
    if (mediaMutation && this.destructiveResetInProgress) throw new Error('media_preparation_blocked_by_reset');
    if (mediaMutation) this.mediaMutationsInFlight++;
    try {
    const adapterAtStart = this.adapter!;
    const pendingOrigin = options.mediaPending ? options.mediaPending.pendingOrigin : options.pendingOrigin !== undefined
      ? options.pendingOrigin
      : capturePendingWorkOrigin();
    const partnerBaseline = pendingOrigin ? readPartnerChecklistBaseline(empresaId, pendingOrigin.userId, checklistId) : null;
    const partnerRevision = partnerBaseline && (partnerBaseline.enabled || partnerBaseline.touched) ? partnerBaseline.revision : undefined;
    const canAcknowledge = options.canAcknowledge ?? (pendingOrigin ? () => {
      const current = capturePendingWorkOrigin();
      return current?.userId === pendingOrigin.userId && current.empresaId === pendingOrigin.empresaId;
    } : undefined);
    const tableName = APP_CONSTANTS.DATABASE.TABLES.GUINCHO_VALORES_CHECKLIST.NAME;

    const changedCampoIds = [...new Set(Object.keys(changes).map(key => observationFieldId(key) ?? key))];
    const sampleCampoIds = changedCampoIds.slice(0, 5);
    bulkUpsertChecklistValuesLogger.info(
      `Bulk upsert de valores do checklist ${checklistId} com ${changedCampoIds.length} campo(s)` +
      ` | skipPendingOp=${Boolean(options.skipPendingOp)}` +
      `${sampleCampoIds.length > 0 ? ` | amostra campos: ${sampleCampoIds.join(', ')}` : ''}`
    );

    if (!options.mediaPending && isLogBufferEnabled()) {
      const localEntries = Object.entries(changes).filter(([, valor]) => containsLocalChecklistValue(valor));
      if (localEntries.length > 0) {
        const campos = localEntries.map(([campo_id, valor]) => ({ campo_id, valor }));
        recordBufferedLogEvent(
          'warn',
          'CHECKLIST_VALUES',
          `Detectado valor local no payload do checklist ${checklistId}`,
          { checklistId, empresaId, campos }
        );
        await ChecklistLogBufferService.reportLocalUriPayload({
          checklistId,
          empresaId,
          campos,
        });
      }
    }

    

    options.mediaPending?.onStage?.('prepare_reading');
    // 1. Buscar registros existentes e identidades historicas incluindo created_at
    const existentes = (await this.listItems<any>(tableName, { checklist_id: checklistId }))
      .filter(row => row.empresa_id === empresaId);
    // Cada chave alterada pode ser um campo corrente ou uma identidade histórica.
    // A consulta por ID evita carregar o catálogo inteiro da empresa a cada edição.
    const snapshotFields = (await Promise.all(changedCampoIds.map(id => this.readItem<any>(
      APP_CONSTANTS.DATABASE.TABLES.GUINCHO_CHECKLIST_SNAPSHOT_FIELDS.NAME,
      id
    )))).filter(field => field?.empresa_id === empresaId);
    const snapshotById = new Map(snapshotFields.map(field => [String(field.id), field]));

    // 2. Montar as linhas mantendo created_at ou usando now
    const now = Date.now();
    const pendingChanges: Record<string, string | ChecklistAnswerPatch> = {};
    const rows = changedCampoIds.map(visualFieldId => {
      const snapshotField = snapshotById.get(visualFieldId);
      const originalCampoId = snapshotField?.original_campo_id ?? null;
      const exist = snapshotField
        ? existentes.find(row => row.snapshot_field_id === visualFieldId)
          ?? (originalCampoId
            ? existentes.find(row => !row.snapshot_field_id && row.campo_id === originalCampoId)
            : undefined)
          ?? existentes.find(row => !row.snapshot_field_id && row.campo_id === visualFieldId)
        : existentes.find(row => !row.snapshot_field_id && row.campo_id === visualFieldId);
      const noteKey = `observacao:${visualFieldId}`;
      const hasNote = Object.prototype.hasOwnProperty.call(changes, noteKey);
      const hasValue = Object.prototype.hasOwnProperty.call(changes, visualFieldId);
      const valor = hasValue ? changes[visualFieldId] : exist?.valor ?? null;
      const observacao = hasNote ? changes[noteKey] || null : exist?.observacao ?? null;
      if (hasNote && changes[noteKey].length > 10000) throw new Error('Observação excede o limite de 10000 caracteres.');
      pendingChanges[visualFieldId] = hasNote || partnerRevision !== undefined
        ? { ...(hasValue ? { valor } : {}), ...(hasNote ? { observacao } : {}),
          ...(partnerRevision !== undefined ? { expected_partner_revision: partnerRevision } : {}) }
        : valor;
      return {
        id: exist?.id ?? uuidv4(),
        empresa_id: empresaId,
        checklist_id: checklistId,
        campo_id: snapshotField ? originalCampoId : visualFieldId,
        ...(snapshotField ? { snapshot_field_id: visualFieldId } : {}),
        ...(hasValue || !hasNote ? { valor } : {}),
        ...(hasNote ? { observacao } : {}),
        created_at: exist?.created_at ?? now,
        updated_at: now,
      };
    });

    bulkUpsertChecklistValuesLogger.info(
      `Inserindo ${rows.length} row(s) localmente preservando created_at para checklist ${checklistId}`
    );

    // 3. Persistir valores e outbox na mesma transação quando o adapter suporta.
    await debugPause('bulkUpsertChecklistValues - antes de chamar o adapter');
    const atomicAdapter = adapterAtStart as IDatabaseAdapter & {
      bulkUpsertWithPendingOperation?: (
        collection: string,
        rows: any[],
        pending: {
          operation: 'checklist_partial_update';
          itemId: string;
          data: string;
          pendingOrigin?: PendingWorkOrigin | null;
          canAcknowledge?: () => boolean;
        }
      ) => Promise<void>;
    };

    if (options.mediaPending) {
      if (!options.skipPendingOp) {
        throw new Error('Mídia local exige fila de imagem sem operação parcial remota.');
      }
      const callerGuard = options.mediaPending.canAcknowledge;
      const mediaGuard = () => {
        const current = capturePendingWorkOrigin();
        return this.adapter === adapterAtStart && !!pendingOrigin &&
          current?.userId === pendingOrigin.userId && current?.empresaId === pendingOrigin.empresaId &&
          empresaId === pendingOrigin.empresaId && (!canAcknowledge || canAcknowledge()) &&
          (!callerGuard || callerGuard());
      };
      if (!mediaGuard()) throw new Error('session_changed_before_checklist_media_service');
      await adapterAtStart.persistChecklistMediaPending(tableName, rows, {
        ...options.mediaPending,
        canAcknowledge: mediaGuard,
        ...(options.mediaPending.clearReference ? { logicalChanges: JSON.stringify(pendingChanges) } : {}),
        pending: options.mediaPending.pending ? { ...options.mediaPending.pending,
          ...(partnerRevision !== undefined ? { expected_partner_revision: partnerRevision } : {}) } : null,
      });
      return;
    } else if (!options.skipPendingOp && atomicAdapter.bulkUpsertWithPendingOperation) {
      bulkUpsertChecklistValuesLogger.info(`Registrando pendência checklist_partial_update atomicamente para checklist ${checklistId}`);
      await atomicAdapter.bulkUpsertWithPendingOperation(tableName, rows, {
        operation: 'checklist_partial_update',
        itemId: checklistId,
        data: JSON.stringify(pendingChanges),
        pendingOrigin,
        canAcknowledge,
      });
    } else {
      await this.adapter!.bulkUpsert(tableName, rows, true);
      if (!options.skipPendingOp) {
        bulkUpsertChecklistValuesLogger.info(`Adapter sem API atômica; registrando pendência separadamente para checklist ${checklistId}`);
        await this.registerPendingOperation(
          'checklist_partial_update',
          tableName,
          checklistId,
          JSON.stringify(pendingChanges)
        );
      }
    }

    if (options.skipPendingOp) {
      bulkUpsertChecklistValuesLogger.info(`Pendência checklist_partial_update não registrada para checklist ${checklistId}`);
    }

    // Mantém rastreabilidade sem despejar payloads gigantes no log.
    const guinchoValores = await this.listItems<any>(tableName, { checklist_id: checklistId });
    const pendingTable = this.getTableName('PENDING_OPERATIONS');
    const pendingOps = await this.listItems<any>(pendingTable, { item_id: checklistId });
    bulkUpsertChecklistValuesLogger.info(
      `Estado após bulkUpsertChecklistValues | valores locais=${guinchoValores.length} | pendências relacionadas=${pendingOps.length}`
    );
    await debugPause('bulkUpsertChecklistValues - após PRINTS estruturados');
    } finally {
      if (mediaMutation) this.mediaMutationsInFlight--;
    }
  }
}

// Exportar uma instância singleton do serviço
export const databaseService = new DatabaseService();

export default databaseService; 
